import React, { useEffect, useMemo, useState } from "react"; // eslint-disable-next-line no-restricted-imports
import { useBreakpointValue } from "@chakra-ui/react";
import {
  Col,
  Row,
  UiCenter,
  UiDivider,
  UiIcon,
  UiImage,
  UiPrimaryButton,
  UiSimpleGrid,
  UiSkeletonText,
  UiText,
  UiVStack,
} from "~/components/elements";
import { useAppDispatch, useRequestState } from "~/hooks";
import { createPaymentSession, fetchPackages } from "~/redux/dashboards/thunk";
import { RootState } from "~/redux/root-reducer";
import { useSelector } from "react-redux";
import { requestOK } from "redux-thunk-kit";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppError } from "ry-error-wrapper";
import { appConfig } from "~/config";
import { PAGES } from "~/constants";
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { DashboardStepper } from "~/components/shared/DashboardStepper";
import basic from "~/assets/img/v2/packages/basic2.png";
import pro from "~/assets/img/v2/packages/pro.png";
import standard from "~/assets/img/v2/packages/standard2.png";
import team from "~/assets/img/v2/packages/team.png";
import guard from "~/assets/img/v2/packages/guard.png";
import { AiFillCheckCircle } from "react-icons/ai";
import { ShiningIcon } from "~/components/icons/SvgIcons";
import { PackageKey, PhotoshootType } from "~/types/models/dashboard";
import { setActiveStep, setSelectedType } from "~/redux/dashboards/slice";
import { PAYMENT_TYPE_JOB } from "~/constants/payment";

export const PACKAGE_STYLE_MAP: Record<PackageKey, { iconColor: string; mainIcon: any }> = {
  "str-basic": { iconColor: "#FFD000", mainIcon: basic },
  "two-person-basic": { iconColor: "#FFD000", mainIcon: basic },
  "str-standard": { iconColor: "#7BC429", mainIcon: standard },
  "two-person-standard": { iconColor: "#7BC429", mainIcon: standard },
  "str-pro": { iconColor: "red", mainIcon: pro },
  "two-person-pro": { iconColor: "red", mainIcon: pro },
  "team-plan": { iconColor: "blue", mainIcon: team },
};

interface PlanCardProps {
  planKey: PackageKey;
  name: string;
  price?: number | string;
  priceUnit?: string;
  descriptions: string[];
  isPopular?: boolean;
  selectedPackage?: string;
  onSelectPackage: (key: PackageKey) => void;
  packageStyleMap: Record<
    string,
    {
      mainIcon: string;
      iconColor: string;
    }
  >;
  position?: number;
  packType?: string;
}

const PlanCard: React.FC<PlanCardProps> = ({
  planKey,
  name,
  price,
  packType,
  descriptions,
  isPopular = false,
  selectedPackage,
  onSelectPackage,
  packageStyleMap,
  position,
}) => {
  const isSelected = selectedPackage === planKey;
  const priceUnit = packType === "two_person" ? "for a two person photoshoot" : "/ person";

  return (
    <Col
      key={position || planKey}
      bg={isSelected ? "lightblue" : "neutral.gray00"}
      borderColor={isSelected ? "blue" : "transparent"}
      borderWidth="1px"
      rounded="3xl"
      cursor="pointer"
      px={2}
      py={{ base: 4, md: 6 }}
      onClick={() => onSelectPackage(planKey)}
      minW="12rem"
      minH="30rem"
      pos="relative"
    >
      {isPopular && (
        <Row pos="absolute" right={0} top={0} bg="#ABD439" py={1} px={4} roundedTopRight="3xl" roundedBottomLeft="3xl">
          <ShiningIcon boxSize={5} />
          <UiText vr="title2" ml={1}>
            MOST POPULAR
          </UiText>
        </Row>
      )}

      <Row align="center">
        <UiImage src={packageStyleMap[planKey]?.mainIcon} width="2.5rem" />
        <UiText vr="headline5" ml={3}>
          {name}
        </UiText>
      </Row>

      <Row align="center" mt={4}>
        <UiText vr="headline3">{typeof price === "number" ? `$${price}` : price}</UiText>
        {/* {typeof price === "number" && ( */}
        {/*  <UiText vr="body2" color="neutral.gray400"> */}
        {/*    &nbsp;{priceUnit} */}
        {/*  </UiText> */}
        {/* )} */}
      </Row>

      <UiVStack
        mt={{ base: 4, md: 6 }}
        spacing={{ base: 4, md: 6 }}
        align="flex-start"
        divider={<UiDivider borderColor="neutral.gray100" ml="1.2rem !important" opacity={1} />}
      >
        {descriptions.map((desc, idx) => (
          <Row key={idx}>
            <UiIcon color={packageStyleMap[planKey]?.iconColor} as={AiFillCheckCircle} boxSize={5} mx={0} />
            <UiText vr="body2" ml={2}>
              {desc}
            </UiText>
          </Row>
        ))}
      </UiVStack>
    </Col>
  );
};

export function SelectPackagePage() {
  const dispatch = useAppDispatch();
  const vrTitle = useBreakpointValue<any>({ base: "headline4", md: "headline2" });
  const vrGuarantee = useBreakpointValue<any>({ base: "body3", md: "body2" });
  const allPackageData = useSelector((state: RootState) => state.dashboard.packageData);
  const selectedType = useSelector((state: RootState) => state.dashboard.selectedType);
  const [selectedPackage, setSelectedPackage] = useState<PackageKey>();
  const { loading: loadingPayment } = useRequestState(createPaymentSession);
  const { loading: loadingPackages } = useRequestState(fetchPackages);

  // Filter package data based on selected type
  const data = useMemo(() => allPackageData.filter(pack => pack.type === selectedType), [allPackageData, selectedType]);

  useEffect(() => {
    dispatch(setActiveStep({ step: 0 }));
  }, [dispatch]);

  const onSelectPackage = async (packageKey: PackageKey) => {
    if (packageKey === selectedPackage) {
      setSelectedPackage(undefined);
    } else {
      setSelectedPackage(packageKey);
    }
  };

  const onSelectType = (type: PhotoshootType) => {
    dispatch(setSelectedType(type));
    setSelectedPackage(undefined); // Reset selected package when switching types
  };

  const onClickNext = async (packageKey: PackageKey) => {
    if (packageKey === "team-plan") {
      window.open("mailto:hello@bettershot?subject=Group Photoshoot Pricing Inquiry", "mail");
      return;
    }
    // cancel_url: `${appConfig.url}${PAGES.PAYMENT_CANCELLED}?session_id={CHECKOUT_SESSION_ID}&package=${packageKey}&job_id=${data?.jobId}&quantity=${data?.quantity}&billing_type=${type}`,

    const resAction = await dispatch(
      createPaymentSession({
        data: {
          cancel_url: `${appConfig.url}${PAGES.PAYMENT_CANCELLED}?session_id={CHECKOUT_SESSION_ID}&package=${packageKey}&billing_type=job`,
          success_url: `${appConfig.url}${PAGES.PAYMENT_SUCCESSFUL}?session_id={CHECKOUT_SESSION_ID}&package=${packageKey}&billing_type=job`,
          package_key: packageKey,
          type: PAYMENT_TYPE_JOB,
        },
      })
    );

    if (requestOK(resAction, createPaymentSession)) {
      const results = unwrapResult(resAction);
      if (results.checkout_url) {
        window.open(results.checkout_url, "_self");
      } else {
        throw new AppError("No checkout URL");
      }
    }
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: 9999, behavior: "smooth" });
  };

  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - Select package`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep
    >
      <Col flex={1} justify="center">
        <UiCenter p={4} gap={5} flexDirection={{ base: "column-reverse", lg: "row" }}>
          <DashboardMainBox px={{ base: 4, md: 5 }}>
            <UiText vr={vrTitle} textAlign="center">
              Select your package
            </UiText>
            <UiText vr="body1" mt={{ base: 2, sm: 3 }} textAlign="center">
              Simple options, each with a 100% money back guarantee.
            </UiText>

            {/* Package Type Selection Buttons */}
            <Row gap={4} mt={{ base: 4, md: 6 }} justify="center" w="full">
              <UiPrimaryButton
                whiteSpace="normal"
                variant={selectedType === "one_person" ? "solid" : "outline"}
                borderColor={selectedType === "one_person" ? undefined : "darkblue"}
                bg={selectedType === "one_person" ? undefined : "transparent"}
                color={selectedType === "one_person" ? undefined : "darkblue"}
                onClick={() => {
                  onSelectType("one_person");
                  setTimeout(scrollToBottom, 0);
                }}
                minW="10rem"
                px={6}
                py={3}
                _hover={{
                  boxShadow: "md",
                }}
              >
                One person photoshoot
              </UiPrimaryButton>
              <UiPrimaryButton
                whiteSpace="normal"
                variant={selectedType === "two_person" ? "solid" : "outline"}
                borderColor={selectedType === "two_person" ? undefined : "darkblue"}
                bg={selectedType === "two_person" ? undefined : "transparent"}
                color={selectedType === "two_person" ? undefined : "darkblue"}
                onClick={() => {
                  onSelectType("two_person");
                  setTimeout(scrollToBottom, 0);
                }}
                minW="10rem"
                px={6}
                py={3}
                _hover={{
                  boxShadow: "md",
                }}
              >
                Two person photoshoot
              </UiPrimaryButton>
            </Row>

            <UiSkeletonText noOfLines={{ base: 4, md: 5 }} skeletonHeight={10} isLoaded={!loadingPackages} w="full">
              <UiSimpleGrid gap={4} w="full" mt={{ base: 4, md: 8 }} minChildWidth="12rem">
                {data.map(pack => (
                  <PlanCard
                    key={pack.position}
                    planKey={pack.key}
                    name={pack.name}
                    price={pack.price}
                    descriptions={pack.meta.description || []}
                    isPopular={pack.is_popular}
                    selectedPackage={selectedPackage}
                    onSelectPackage={onSelectPackage}
                    packageStyleMap={PACKAGE_STYLE_MAP}
                    position={pack.position}
                    packType={pack.type}
                  />
                ))}

                {selectedType === "one_person" && (
                  <PlanCard
                    planKey="team-plan"
                    name="Team Plans"
                    price="Let's talk"
                    descriptions={[
                      "Volume-based pricing and flexible options",
                      "Brand-consistent themes for teams",
                      "Done in 2 hours",
                    ]}
                    selectedPackage={selectedPackage}
                    onSelectPackage={onSelectPackage}
                    packageStyleMap={PACKAGE_STYLE_MAP}
                  />
                )}
              </UiSimpleGrid>
            </UiSkeletonText>
            <Row w="full" mt={{ base: 4, md: 5 }} p={{ base: 4, md: 5 }} bg="neutral.gray00" rounded="3xl">
              <UiImage src={guard} width="6" />
              <Col flex={1} ml={2}>
                <UiText vr="title2">100% money back guarantee</UiText>
                <UiText vr={vrGuarantee} mt={1}>
                  If you followed our best practices and still don&apos;t like any of your photoshoots, we&apos;ll
                  refund your payment.
                </UiText>
              </Col>
            </Row>
            <Row
              gap={{ base: 2, sm: 5 }}
              flexDirection={{ base: "column", md: "row-reverse" }}
              align="center"
              justify="center"
              mt={8}
              w="full"
            >
              <UiPrimaryButton
                w="full"
                maxW={{ base: undefined, md: "16.25rem" }}
                isDisabled={!selectedPackage}
                isLoading={loadingPayment}
                onClick={() => onClickNext(selectedPackage!)}
              >
                Next
              </UiPrimaryButton>
            </Row>
          </DashboardMainBox>
          <DashboardStepper />
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
